import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import DateInput from '@/components/ui/DateInput'
import { settings as api } from '@/api/endpoints'
import { Alert, Field, Spinner } from '@/components/ui'
import { BankIcon, FIO, STATEMENT_BANKS, bankLabel, type Bank } from '@/features/settings/banks'
import { addDays, formatDateTime, formatMoney, toISODate } from '@/lib/utils'
import type { FioImportResult, FioTransaction, Settings, StatementImportResult } from '@/types/api'

/**
 * Import platieb z banky - Fio (API) a výpisy ostatných bánk (súbor).
 *
 * Je na dvoch miestach: v Nastaveniach → Integrácie (správca banky nastavuje)
 * a v Platby → Import z banky (spúšťa ho aj manažér rezervácií - párovanie
 * platieb je denná práca, nie nastavenie). Zobrazí len to, čo je nastavené.
 */
export function BankImports({
  settings,
  onError,
}: {
  settings: Settings
  onError: (text: string) => void
}) {
  return (
    <>
      {settings.modules.fio.configured ? (
        <FioImport lastImport={settings.fio_last_import_at ?? null} onError={onError} />
      ) : null}
      {STATEMENT_BANKS.filter((bank) => settings.modules.statement.banks.includes(bank.code)).map(
        (bank) => (
          <StatementImport
            key={bank.code}
            bank={bank}
            lastImport={settings.statement_imports?.[bank.code] ?? null}
            onError={onError}
          />
        ),
      )}
    </>
  )
}

/** Je čo importovať? */
export function hasBankImports(settings: Settings | undefined) {
  return Boolean(
    settings && (settings.modules.fio.configured || settings.modules.statement.banks.length > 0),
  )
}

function FioImport({
  lastImport,
  onError,
}: {
  lastImport: string | null
  onError: (text: string) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [range, setRange] = useState(() => ({
    from: toISODate(addDays(new Date(), -7)),
    to: toISODate(new Date()),
  }))
  const [result, setResult] = useState<FioImportResult | null>(null)

  const runImport = useMutation({
    mutationFn: () => api.importFio(range),
    onSuccess: (imported) => {
      setResult(imported)
      // Import zapisuje platby - kalendár aj poplatky sa mohli zmeniť.
      void qc.invalidateQueries({ queryKey: ['settings'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      void qc.invalidateQueries({ queryKey: ['fees'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched-count'] })
    },
    onError: (err) => onError(apiErrorMessage(err)),
  })

  return (
    <section className="card space-y-3 p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <BankIcon bank={FIO} size={24} />
        {t('settings.importSection', { bank: bankLabel(FIO) })}
      </h2>

      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('global.from')} className="w-40">
          <DateInput value={range.from} onChange={(from) => setRange({ ...range, from })} />
        </Field>
        <Field label={t('global.to')} className="w-40">
          <DateInput value={range.to} onChange={(to) => setRange({ ...range, to })} />
        </Field>
        <button
          type="button"
          className="btn-secondary"
          disabled={runImport.isPending}
          onClick={() => runImport.mutate()}
        >
          {runImport.isPending ? <Spinner /> : null}
          {t('settings.fioImportButton')}
        </button>
      </div>

      <p className="text-xs text-slate-500">
        {lastImport
          ? t('settings.bankLastImport', { date: formatDateTime(lastImport) })
          : t('settings.bankNeverImported')}
      </p>

      {result ? (
        <div className="space-y-3">
          <Alert variant="success">
            {t('settings.bankResultSummary', {
              matched: result.matched_count,
              unmatched: result.unmatched_count,
              skipped: result.skipped_count,
            })}
          </Alert>

          {result.matched.length > 0 ? (
            <BankTable title={t('settings.bankMatched')} rows={result.matched} />
          ) : null}
          {result.unmatched.length > 0 ? (
            <BankTable
              title={t('settings.bankUnmatched')}
              rows={result.unmatched}
              reasonKey="reason"
            />
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

/** Nahratie výpisu jednej banky a výsledok párovania. */
function StatementImport({
  bank,
  lastImport,
  onError,
}: {
  bank: Bank
  lastImport: string | null
  onError: (text: string) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<StatementImportResult | null>(null)

  const run = useMutation({
    mutationFn: (upload: File) => api.importStatement(bank.code, upload),
    onSuccess: (imported) => {
      setResult(imported)
      setFile(null)
      // Import zapisuje platby - kalendár aj poplatky sa mohli zmeniť.
      void qc.invalidateQueries({ queryKey: ['settings'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
      void qc.invalidateQueries({ queryKey: ['fees'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched-count'] })
    },
    onError: (err) => onError(apiErrorMessage(err)),
  })

  return (
    <section className="card space-y-3 p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <BankIcon bank={bank} size={24} />
        {t('settings.importSection', { bank: bankLabel(bank) })}
      </h2>
      <p className="text-xs text-slate-500">
        {bank.code === 'tatra' ? t('settings.statementHintTatra') : t('settings.statementHint', { bank: bank.name })}
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('settings.statementFile')} className="sm:max-w-sm">
          <input
            type="file"
            accept=".xml,application/xml,text/xml"
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <button
          type="button"
          className="btn-secondary"
          disabled={!file || run.isPending}
          onClick={() => file && run.mutate(file)}
        >
          {run.isPending ? <Spinner /> : null}
          {t('settings.statementImportButton')}
        </button>
      </div>

      <p className="text-xs text-slate-500">
        {lastImport
          ? t('settings.bankLastImport', { date: formatDateTime(lastImport) })
          : t('settings.bankNeverImported')}
      </p>

      {result ? (
        <div className="space-y-3">
          <Alert variant="success">
            {t('settings.bankResultSummary', {
              matched: result.matched_count,
              unmatched: result.unmatched_count,
              skipped: result.skipped_count,
            })}
          </Alert>
          <p className="text-xs text-slate-500">
            {t('settings.statementPeriod', {
              account: result.account || '—',
              from: result.from || '—',
              to: result.to || '—',
            })}
          </p>
          {result.other_bank ? (
            <Alert variant="warning">
              {t('settings.statementOtherBank', { detected: result.other_bank, bank: bank.name })}
            </Alert>
          ) : null}
          {result.other_account ? (
            <Alert variant="warning">{t('settings.statementOtherAccount')}</Alert>
          ) : null}
          {result.matched.length > 0 ? (
            <BankTable title={t('settings.bankMatched')} rows={result.matched} />
          ) : null}
          {result.unmatched.length > 0 ? (
            <BankTable title={t('settings.bankUnmatched')} rows={result.unmatched} reasonKey="reason" />
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function BankTable({
  title,
  rows,
  reasonKey,
}: {
  title: string
  rows: FioTransaction[]
  reasonKey?: 'reason'
}) {
  const { t } = useTranslation()
  const reasonLabel = (row: FioTransaction) => {
    const reason = reasonKey ? (row as FioTransaction & { reason: string })[reasonKey] : null
    if (reason === 'no_match') return t('settings.bankReasonNoMatch')
    if (reason === 'no_due') return t('settings.bankReasonNoDue')
    return '—'
  }

  return (
    <div className="overflow-x-auto">
      <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">{title}</h3>
      <table className="table-base">
        <thead>
          <tr>
            <th>{t('settings.bankColDate')}</th>
            <th className="text-right">{t('settings.bankColAmount')}</th>
            <th>{t('settings.bankColVs')}</th>
            <th>{t('settings.bankColCounterparty')}</th>
            {reasonKey ? <th>{t('settings.bankColReason')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.transaction_id ?? index}>
              <td className="whitespace-nowrap text-slate-500">{row.date || '—'}</td>
              <td className="text-right tabular-nums">{formatMoney(row.amount)}</td>
              <td className="font-mono text-xs">{row.variable_symbol || '—'}</td>
              <td className="text-slate-500">{row.counterparty || '—'}</td>
              {reasonKey ? <td className="text-xs text-amber-700">{reasonLabel(row)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
